import { memo, useMemo, useState } from 'react';
import styles from './CaseLeadCard.module.css';
import caseIcon from '../../assets/Icons/caseIcon.svg?url';

interface CaseLeadCardProps {
  position: number;
  name: string;
  rewardNumber: number;
  photoUrl: string;
  isYou?: boolean;
  open?: number;
}

const CaseLeadCardInner = ({
  position,
  name,
  rewardNumber,
  photoUrl,
  isYou,
  open,
}: CaseLeadCardProps) => {
  const [imageError, setImageError] = useState(false);
  const getFirstLetter = (title: string) => {
    // Регулярное выражение для проверки эмодзи
    const emojiRegex =
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE00}-\u{FE0F}]|[\u{1FAB0}-\u{1FABF}]|[\u{1FAC0}-\u{1FAFF}]|[\u{1FAD0}-\u{1FAFF}]|[\u{1FAE0}-\u{1FAFF}]|[\u{1FAF0}-\u{1FAFF}]/u;

    // Проверяем, содержит ли заголовок эмодзи
    if (emojiRegex.test(title)) {
      // Если есть эмодзи, берем второй символ (пропускаем эмодзи)
      return title.slice(1, 2);
    } else {
      // Если нет эмодзи, берем первую букву
      return title.slice(0, 1);
    }
  };

  const amount = useMemo(
    () => Math.round(rewardNumber).toString(),
    [rewardNumber],
  );
  return (
    <div
      className={
        isYou
          ? styles.leadCardYou
          : position === 1
            ? styles.firstPlace
            : position === 2
              ? styles.secondPlace
              : position === 3
                ? styles.thirdPlace
                : styles.leadCard
      }
    >
      <div
        className={
          isYou
            ? styles.position
            : position === 1
              ? styles.firstPosition
              : position === 2
                ? styles.secondPosition
                : position === 3
                  ? styles.thirdPosition
                  : styles.position
        }
      >
        {position === 0 ? '100+' : `${position}.`}
      </div>
      <div className={position === 100 ? styles.userData100 : styles.userData}>
        {!photoUrl || imageError ? (
          <div className={styles.userDataPhoto}>{getFirstLetter(name)}</div>
        ) : (
          <img
            src={photoUrl}
            alt="avatar"
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
          />
        )}
        <div
          className={
            isYou
              ? styles.userName
              : position === 1
                ? styles.firstPlaceName
                : position === 2
                  ? styles.secondPlaceName
                  : position === 3
                    ? styles.thirdPlaceName
                    : styles.userName
          }
        >
          {name}
        </div>
      </div>

      {/* Иконка сейфа */}
      <div className={styles.caseIconContainer}>
        <img src={caseIcon} alt="case" className={styles.caseIcon} />
        <div className={styles.caseOpenNumber}>{open}</div>
      </div>

      <div className={styles.rewardNumberContainer}>
        <div className={styles.rewardIcon}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.85413 0.561185L10.5804 4.24782C10.7102 4.52547 10.9609 4.71749 11.2515 4.76173L15.2364 5.37157C15.9671 5.48357 16.2587 6.43231 15.7298 6.97632L12.8489 9.93638C12.6382 10.1529 12.5422 10.4644 12.592 10.7703L13.2506 14.8212C13.3839 15.6428 12.5688 16.2696 11.8702 15.8828L8.41412 13.9675C8.15456 13.8235 7.84519 13.8235 7.58652 13.9675L4.13309 15.8809C3.43352 16.2687 2.6157 15.6409 2.74992 14.8174C2.74992 14.8174 2.66667 12.4444 3.55556 11.5556C4.44444 10.6667 8.56883 9.3493 8.44444 8.88889C8.32006 8.42848 3.73003 9.41829 2.74992 9.09028C1.76981 8.76227 0.270803 6.97632 0.270803 6.97632C-0.258988 6.43231 0.032514 5.48357 0.764087 5.37157L4.7491 4.76173C5.03889 4.71749 5.28956 4.52547 5.42023 4.24782L7.14651 0.561185C7.49408 -0.187062 8.5039 -0.187062 8.85413 0.561185Z"
              fill={
                (position === 2 || position === 3) && !isYou
                  ? 'black'
                  : 'url(#paint0_linear_4001_293)'
              }
            />
            <defs>
              <linearGradient
                id="paint0_linear_4001_293"
                x1="8"
                y1="0.121528"
                x2="8"
                y2="16.1215"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="#FFC430" />
                <stop offset="1" stop-color="#FF9E0E" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <p
          className={
            isYou
              ? styles.rewardNumberisYou
              : position === 1
                ? styles.firstRewardNumber
                : position === 2
                  ? styles.secondRewardNumber
                  : position === 3
                    ? styles.thirdRewardNumber
                    : styles.rewardNumber
          }
        >
          {amount}
        </p>
      </div>
    </div>
  );
};

const CaseLeadCard = memo(
  CaseLeadCardInner,
  (prev, next) =>
    prev.position === next.position &&
    prev.name === next.name &&
    prev.rewardNumber === next.rewardNumber &&
    prev.photoUrl === next.photoUrl &&
    prev.isYou === next.isYou &&
    prev.open === next.open,
);

export default CaseLeadCard;
